import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, MenuItem, Typography, Alert } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiFormSelect from "src/components/core-components/mui/ByzMuiFormSelect";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import { referenceService } from "src/api/references/referenceService";
import { WillOpenCardKey } from "src/api/request/requestService";
import { useTranslate } from "src/locales";
import { useAppSelector } from "src/app/hooks";
import { RootState } from "src/app/store";

interface HIVModalProps {
  open: boolean;
  onClose: () => void;
  serviceName?: string;
  onSave?: (processInfo: string) => void;
  // Merkezi additional-info fetch için opsiyonel parametreler
  additionalInfoKey?: WillOpenCardKey;
  additionalInfoParams?: {
    serviceId: number;
    requestDetailId: number;
    ptId?: number;
    officeId?: number;
  };
}

const HIVModal: React.FC<HIVModalProps> = ({
  open,
  onClose,
  serviceName = "Seçilen Hizmetin Adı",
  onSave,
  additionalInfoKey,
  additionalInfoParams,
}) => {
  const { t } = useTranslate();
  const [processInfo, setProcessInfo] = useState<string>("");
  const [options, setOptions] = useState<{ label: string; value: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState<any>(null);
  const { selectedOfficeId } = useAppSelector((state: RootState) => ({
    selectedOfficeId: state.auth.selectedOfficeId,
  }));

  // AdditionalInfo/fields üzerinden HIV seçeneklerini yükle
  useEffect(() => {
    if (!open) return;
    setProcessInfo("");
    setError("");

    const fetchOptions = async () => {
      try {
        setLoading(true);

        // Backend kontratına göre formtype sabit "HIV"
        const res = await referenceService.additionalInfo.getFields({
          formtype: "HIV",
          offid:
            additionalInfoParams?.officeId ??
            (selectedOfficeId ? Number(selectedOfficeId) : undefined),
        });
        if (res) {
          setAdditionalInfo(res);
        }


        const field = res.fields && res.fields.length > 0 ? res.fields[0] : null;
        if (field && Array.isArray(field.options)) {
          setOptions(
            field.options.map((opt) => ({
              label: opt.label ?? "",
              value: String(opt.value),
            }))
          );
        } else {
          setOptions([]);
          setError("HIV seçenekleri bulunamadı.");
        }
      } catch (err) {
        setOptions([]);
        setError("İşlem listesi yüklenemedi.");
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, [open, additionalInfoParams, selectedOfficeId]);

  const handleSave = () => {
    if (!processInfo) return;
    onSave?.(processInfo);
    if (!onSave) {
      onClose();
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          mx: -2,
          mt: 4,
          mb: 2,
          py: 1.5,
          px: 2,
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" fontWeight={700} color="#111827">
          {t("patient-request.hiv.title")}
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={!processInfo} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.hiv.processed")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzSelectListPopupInput
              fullWidth
              size="small"
              label={t("patient-request.hiv.processed")}
              value={
                options.find((opt) => opt.value === processInfo)?.label || ""
              }
              selectedId={processInfo || undefined}
              dialogMaxHeight="60vh"
              staticData={options.map((opt) => ({
                id: opt.value,
                NAME: opt.label,
              }))}
              displayField="NAME"
              disabled={loading}
              onChange={(rows) => {
                const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                const first = list[0];
                if (first) {
                  setProcessInfo(String(first.id));
                }
              }}
            />
          </Grid>
        </Grid>

      </Box>
    </OpenComponentDialog>
  );
};

export default HIVModal;
